import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  DAYANAK_ETIKETI,
  DURUM_ETIKETI,
  onIzinListesi,
  onIzinOnayBekleyenler,
  onIzinOnKontrol,
  onIzinsizOran,
  onIzinTalepEt,
  type OnIzinDayanagi,
  type OnIzinDurumu,
  type OnIzinOnKontrolSonucu,
  type OnIzinOzeti,
  type OnIzinsizOranSatiri,
} from '../api/on-izin';
import type { Kimlik } from '../api/tipler';
import { BirimSecici } from '../bilesenler/BirimSecici';
import { Diyalog } from '../bilesenler/Diyalog';
import { Tablo, type Sutun } from '../bilesenler/Tablo';
import { CokSatir, Dugme, HataKutusu, Metin, Sekme } from '../bilesenler/temel';
import { bugun, gunEkle, tarihMetni, zamanMetni } from '../bicim/tarih';
import { onIzinKararKullan } from '../kancalar/on-izin-karar';
import { yetkiKullan, yetkilerHazirKullan } from '../yetki/yetki';

/**
 * MESAİ ÖN İZNİ.
 *
 * ÖNCE ÖN İZİN, SONRA MESAİ. Sıra tersine çevrilirse "ön izinsiz mesai
 * oranı" göstergesi anlamını yitirir — o gösterge pilotun temel ölçütü ve
 * fazla çalışmanın istisna mı kural mı olduğunu söylüyor.
 *
 * TALEP EDEN İLE ONAYLAYAN AYRI (4857/41): fazla çalışma onayı işverenin,
 * talep birim yöneticisinin. `MESAI_ON_IZIN_TALEP` şefte,
 * `MESAI_ON_IZIN_ONAY` müdürde. Aynı kişide olsaydı fazla çalışma tek
 * imzayla açılır ve 4857/41'in aradığı işveren onayı fiilen kalkardı.
 */

const DURUM_BICIMI: Record<OnIzinDurumu, string> = {
  talep: 'bg-amber-50 text-amber-800 ring-amber-200',
  onaylandi: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  reddedildi: 'bg-red-50 text-red-700 ring-red-200',
  iptal: 'bg-slate-50 text-slate-500 ring-slate-200',
};

const DURUMLAR: OnIzinDurumu[] = ['talep', 'onaylandi', 'reddedildi', 'iptal'];

function DurumRozeti({ durum }: { durum: OnIzinDurumu }) {
  return (
    <span
      className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${DURUM_BICIMI[durum]}`}
    >
      {DURUM_ETIKETI[durum]}
    </span>
  );
}

type SekmeKodu = 'liste' | 'onay' | 'rapor';

export function MesaiOnIzni() {
  const yetkilerHazir = yetkilerHazirKullan();

  const listeGorebilir = yetkiKullan(
    'MESAI_ON_IZIN_TALEP',
    'MESAI_ON_IZIN_ONAY',
    'RAPOR_GORUNTULE',
  );
  const onaylayabilir = yetkiKullan('MESAI_ON_IZIN_ONAY');
  const talepEdebilir = yetkiKullan('MESAI_ON_IZIN_TALEP');
  const raporGorebilir = yetkiKullan('MESAI_ON_IZIN_ONAY', 'RAPOR_GORUNTULE', 'DENETIM_EKRANI');

  const [sekmeSecimi, sekmeSecimiYaz] = useState<SekmeKodu | null>(null);
  const sekme: SekmeKodu =
    sekmeSecimi ?? (listeGorebilir ? 'liste' : onaylayabilir ? 'onay' : 'rapor');

  const [formAcik, formAcikYaz] = useState(false);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Mesai ön izni</h1>
          <p className="mt-1 text-sm text-slate-600">
            Fazla çalışma ÖNCEDEN izin alınarak yapılır (4857/41). Talep birim yöneticisinin, onay
            işverenin; ikisi aynı kişi olamaz.
          </p>
        </div>

        {talepEdebilir && <Dugme tikla={() => formAcikYaz(true)} cocuk="Ön izin talep et" />}
      </header>

      <div className="flex gap-1 border-b border-slate-200">
        {listeGorebilir && (
          <Sekme etkin={sekme === 'liste'} tikla={() => sekmeSecimiYaz('liste')} etiket="Tümü" />
        )}
        {onaylayabilir && (
          <Sekme
            etkin={sekme === 'onay'}
            tikla={() => sekmeSecimiYaz('onay')}
            etiket="Onay bekleyenler"
          />
        )}
        {raporGorebilir && (
          <Sekme
            etkin={sekme === 'rapor'}
            tikla={() => sekmeSecimiYaz('rapor')}
            etiket="Ön izinsiz oran"
          />
        )}
      </div>

      {!yetkilerHazir ? (
        <p className="text-sm text-slate-500">Yetkiler yükleniyor…</p>
      ) : sekme === 'liste' && listeGorebilir ? (
        <TumListe onaylayabilir={onaylayabilir} />
      ) : sekme === 'onay' && onaylayabilir ? (
        <OnayKuyrugu />
      ) : raporGorebilir ? (
        <OranRaporu />
      ) : (
        <p className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
          Bu ekranı görüntüleme yetkiniz yok.
        </p>
      )}

      {formAcik && <TalepDiyalogu kapat={() => formAcikYaz(false)} />}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function sutunlariUret(
  onaylayabilir: boolean,
  eylem: (o: OnIzinOzeti, tur: 'onay' | 'red') => void,
): Sutun<OnIzinOzeti>[] {
  return [
    {
      baslik: 'Tarih',
      hucre: (o) => (
        <span className="whitespace-nowrap text-xs">
          <span className="block font-medium text-slate-900">
            {tarihMetni(o.baslangicTarihi)} – {tarihMetni(o.bitisTarihi)}
          </span>
          <span className="block text-slate-500">{zamanMetni(o.talepZamani)}</span>
        </span>
      ),
      daralt: true,
    },
    { baslik: 'Birim', hucre: (o) => o.birimAd },
    {
      baslik: 'Tahmin',
      // Saat METİN (`2.50`) — sayıya çevrilirse yuvarlanır.
      hucre: (o) => (
        <span className="whitespace-nowrap text-xs">
          {o.tahminiSaat} sa × {o.tahminiKisi} kişi
        </span>
      ),
      daralt: true,
      hizala: 'sag',
    },
    {
      baslik: 'Tutar',
      /*
       * TAHMİNİ TUTAR VE BÜTÇE ORANI. Ön izin isteyen yönetici maliyeti
       * görmeden karar veremez; onaylayan da bütçeye etkisini bilmeli.
       * Değer METİN olarak basılır.
       */
      hucre: (o) => (
        <span className="whitespace-nowrap text-xs">
          <span className="block font-medium text-slate-900">{o.tahminiTutar ?? '—'}</span>
          {o.donemButceOrani !== null && (
            <span className="block text-slate-500">bütçe %{o.donemButceOrani}</span>
          )}
        </span>
      ),
      daralt: true,
      hizala: 'sag',
    },
    {
      baslik: 'Kota',
      /*
       * EN YÜKSEK KOTA ORANI: ekipte 270 saate en yakın kişinin durumu.
       * Ortalama gösterilseydi kotası dolmak üzere olan kişi görünmez ve
       * onay yasa dışı bir mesaiye yol açabilirdi (4857/41).
       */
      hucre: (o) =>
        o.enYuksekKotaOrani === null ? (
          '—'
        ) : (
          <span
            className={`whitespace-nowrap text-xs ${
              Number(o.enYuksekKotaOrani) > 80 ? 'font-medium text-red-700' : 'text-slate-600'
            }`}
          >
            %{o.enYuksekKotaOrani}
          </span>
        ),
      daralt: true,
      hizala: 'sag',
    },
    {
      baslik: 'Dayanak',
      hucre: (o) => (
        <span className="text-xs">
          <span className="block">{DAYANAK_ETIKETI[o.dayanak]}</span>
          {o.fazlaCalismaOnayiVar && <span className="block text-emerald-700">işçi onayı var</span>}
        </span>
      ),
    },
    {
      baslik: 'Talep eden',
      hucre: (o) => <span className="text-xs">{o.talepEdenAdSoyad}</span>,
      daralt: true,
    },
    { baslik: 'Durum', hucre: (o) => <DurumRozeti durum={o.durum} />, daralt: true },
    {
      baslik: '',
      hucre: (o) =>
        // Yalnız TALEP durumunda karar verilir; onaylanmış ön izin geri alınmaz.
        onaylayabilir && o.durum === 'talep' ? (
          <span className="flex justify-end gap-1 whitespace-nowrap">
            <button
              type="button"
              onClick={() => eylem(o, 'onay')}
              className="rounded px-2 py-1 text-xs text-kurum-700 hover:bg-kurum-50"
            >
              Onayla
            </button>
            <button
              type="button"
              onClick={() => eylem(o, 'red')}
              className="rounded px-2 py-1 text-xs text-red-700 hover:bg-red-50"
            >
              Reddet
            </button>
          </span>
        ) : null,
      hizala: 'sag',
      daralt: true,
    },
  ];
}

/* ------------------------------------------------------------------ */

function TumListe({ onaylayabilir }: { onaylayabilir: boolean }) {
  const [birimId, birimIdYaz] = useState<Kimlik | undefined>(undefined);
  const [durum, durumYaz] = useState<OnIzinDurumu | ''>('');
  const { eylemYaz, diyalog } = onIzinKararKullan();

  const liste = useQuery({
    queryKey: ['on-izin', 'liste', { birimId, durum }],
    queryFn: () => onIzinListesi({ birimId, durum: durum === '' ? undefined : durum }),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-64">
          <BirimSecici
            deger={birimId}
            degisti={birimIdYaz}
            bosSecenek="Kapsamımdaki tüm birimler"
          />
        </div>
        <label className="text-sm text-slate-700">
          <span className="mb-1 block font-medium">Durum</span>
          <select
            value={durum}
            onChange={(olay) => durumYaz(olay.target.value as OnIzinDurumu | '')}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100"
          >
            <option value="">Tümü</option>
            {DURUMLAR.map((d) => (
              <option key={d} value={d}>
                {DURUM_ETIKETI[d]}
              </option>
            ))}
          </select>
        </label>
      </div>

      <HataKutusu hata={liste.error} />

      <Tablo
        sutunlar={sutunlariUret(onaylayabilir, (o, tur) => eylemYaz({ o, tur }))}
        satirlar={liste.data ?? []}
        anahtar={(o) => o.onIzinId}
        bekliyor={liste.isPending}
        bosMetin="Ön izin kaydı yok."
      />

      {diyalog}
    </div>
  );
}

function OnayKuyrugu() {
  const { eylemYaz, diyalog } = onIzinKararKullan();

  const liste = useQuery({
    queryKey: ['on-izin', 'onay-bekleyenler'],
    queryFn: () => onIzinOnayBekleyenler(),
  });

  return (
    <div className="space-y-2">
      <p className="text-xs text-slate-500">
        Onay ZORUNLU BİR ADIMDIR: ön izinsiz gerçekleşen mesai göstergeye girer ve pilot
        değerlendirmesinde süreç işlemiyor sayılır.
      </p>
      <HataKutusu hata={liste.error} />
      <Tablo
        sutunlar={sutunlariUret(true, (o, tur) => eylemYaz({ o, tur }))}
        satirlar={liste.data ?? []}
        anahtar={(o) => o.onIzinId}
        bekliyor={liste.isPending}
        bosMetin="Onay bekleyen ön izin yok."
      />
      {diyalog}
    </div>
  );
}

/* ------------------------------------------------------------------ */

/*
 * ORAN RAPORU GÜN SAYAR, SAAT DEĞİL.
 *
 * Uç `onIzinsizGun` / `mesailiGun` döndürüyor. İlk yazımda saat alanları
 * varsayılmıştı; şema düzeltti. Gün bazlı olması tutarlı: ön izin bir
 * TARİH ARALIĞI için alınıyor, saat bazlı verilmiyor — "şu gün mesai
 * yapıldı, izni var mıydı" sorusu gün üzerinden cevaplanır.
 */
const ORAN_SUTUNLARI: Sutun<OnIzinsizOranSatiri>[] = [
  { baslik: 'Birim', hucre: (s) => s.birimAd },
  {
    baslik: 'Mesaili gün',
    hucre: (s) => `${s.mesailiGun} gün`,
    daralt: true,
    hizala: 'sag',
  },
  {
    baslik: 'Ön izinsiz gün',
    hucre: (s) => (
      <span
        className={`whitespace-nowrap font-medium ${
          s.onIzinsizGun > 0 ? 'text-amber-800' : 'text-slate-900'
        }`}
      >
        {s.onIzinsizGun} gün
      </span>
    ),
    daralt: true,
    hizala: 'sag',
  },
  {
    baslik: 'Oran',
    /*
     * ORAN YÜKSEKSE İKİ SEBEP VAR: ya süreç işlemiyor ya mesai plansız
     * yapılıyor. İkisi de yönetim sorunu ve rakamla konuşulmadan
     * tartışılamaz. Değer METİN — eşik karşılaştırması yalnız RENK için.
     */
    hucre: (s) =>
      s.onIzinsizOran === null ? (
        '—'
      ) : (
        <span
          className={`whitespace-nowrap font-medium ${
            Number(s.onIzinsizOran) > 20 ? 'text-red-700' : 'text-slate-700'
          }`}
        >
          %{s.onIzinsizOran}
        </span>
      ),
    daralt: true,
    hizala: 'sag',
  },
];

function OranRaporu() {
  const [baslangic, baslangicYaz] = useState(() => gunEkle(bugun(), -60));
  const [bitis, bitisYaz] = useState(() => bugun());

  const rapor = useQuery({
    queryKey: ['on-izin', 'oran', { baslangic, bitis }],
    queryFn: () => onIzinsizOran(baslangic, bitis),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <Metin etiket="Başlangıç" tur="date" deger={baslangic} degisti={baslangicYaz} />
        <Metin etiket="Bitiş" tur="date" deger={bitis} degisti={bitisYaz} />
      </div>

      <p className="text-xs text-slate-500">
        Hedef sıfıra yakın olmalı. Ön izinsiz mesai, işçi onayı ve dayanak olmadan yapılmış fazla
        çalışmadır (4857/41) ve denetimde savunulamaz.
      </p>

      <HataKutusu hata={rapor.error} />

      <Tablo
        sutunlar={ORAN_SUTUNLARI}
        satirlar={rapor.data ?? []}
        anahtar={(s) => s.birimId}
        bekliyor={rapor.isPending}
        bosMetin="Bu aralıkta mesai kaydı yok."
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Talep formu                                                         */
/* ------------------------------------------------------------------ */

/**
 * ÖN İZİN TALEBİ — ÖN KONTROL OLMADAN KAYDEDİLEMEZ.
 *
 * Ayrı bir uç (`/on-kontrol`) tahmini tutarı, bütçe oranını ve 270 saat
 * kotasına etkisini talebi KAYDETMEDEN döndürüyor. Kaydettikten sonra
 * gösterilseydi geri alma gerekir, geri alma da denetim izine yazılırdı.
 *
 * Panel bu sırayı zorunlu kılıyor: "Etkiyi göster" çağrılmadan Kaydet açılmaz.
 */
function TalepDiyalogu({ kapat }: { kapat: () => void }) {
  const sorguIstemcisi = useQueryClient();

  const [birimId, birimIdYaz] = useState<Kimlik | undefined>(undefined);
  const [baslangicTarihi, baslangicTarihiYaz] = useState(() => bugun());
  const [bitisTarihi, bitisTarihiYaz] = useState(() => bugun());
  const [tahminiSaat, tahminiSaatYaz] = useState('2.00');
  const [tahminiKisi, tahminiKisiYaz] = useState('8');
  const [dayanak, dayanakYaz] = useState<OnIzinDayanagi>('tis_hukmu');
  const [isciOnayi, isciOnayiYaz] = useState(true);
  const [gerekce, gerekceYaz] = useState('');
  const [aciklama, aciklamaYaz] = useState('');

  const [kontrol, kontrolYaz] = useState<{
    /** Ön kontrolün ait olduğu girdi imzası; değişirse sonuç GEÇERSİZ. */
    imza: string;
    sonuc: OnIzinOnKontrolSonucu;
  } | null>(null);

  const girdi = () => ({
    birimId: birimId!,
    baslangicTarihi,
    bitisTarihi,
    tahminiSaat: tahminiSaat.trim(),
    tahminiKisi: Number(tahminiKisi),
    gerekce: gerekce.trim(),
    dayanak,
    fazlaCalismaOnayiVar: isciOnayi,
    aciklama: aciklama.trim() === '' ? undefined : aciklama.trim(),
  });

  const imza = `${birimId}|${baslangicTarihi}|${bitisTarihi}|${tahminiSaat}|${tahminiKisi}`;
  const kontrolGecerli = kontrol !== null && kontrol.imza === imza;

  const izle = useMutation({
    mutationFn: () => onIzinOnKontrol(girdi()),
    onSuccess: (sonuc) => kontrolYaz({ imza, sonuc }),
  });

  const kaydet = useMutation({
    mutationFn: () => onIzinTalepEt(girdi()),
    onSuccess: async () => {
      await sorguIstemcisi.invalidateQueries({ queryKey: ['on-izin'] });
      kapat();
    },
  });

  const alanlarDolu =
    birimId !== undefined &&
    tahminiSaat.trim() !== '' &&
    Number(tahminiKisi) > 0 &&
    gerekce.trim() !== '';

  return (
    <Diyalog
      baslik="Mesai ön izni talep et"
      aciklama="Fazla çalışma önceden izin alınarak yapılır (4857/41). Onay ayrı bir kişide."
      kapat={kapat}
      genis
      cocuk={
        <div className="space-y-4">
          <BirimSecici deger={birimId} degisti={birimIdYaz} kendiBirimiyleBasla />

          <div className="grid gap-4 sm:grid-cols-2">
            <Metin
              etiket="Başlangıç"
              tur="date"
              deger={baslangicTarihi}
              degisti={baslangicTarihiYaz}
            />
            <Metin etiket="Bitiş" tur="date" deger={bitisTarihi} degisti={bitisTarihiYaz} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Metin
              etiket="Kişi başı tahmini saat"
              deger={tahminiSaat}
              degisti={tahminiSaatYaz}
              ipucu="Ondalık NOKTA ile — örn. 2.50"
            />
            <Metin etiket="Tahmini kişi" deger={tahminiKisi} degisti={tahminiKisiYaz} />
          </div>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">Dayanak</span>
            <select
              value={dayanak}
              onChange={(olay) => dayanakYaz(olay.target.value as OnIzinDayanagi)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100"
            >
              {(Object.keys(DAYANAK_ETIKETI) as OnIzinDayanagi[]).map((d) => (
                <option key={d} value={d}>
                  {DAYANAK_ETIKETI[d]}
                </option>
              ))}
            </select>
          </label>

          {/*
            İŞÇİ ONAYI 4857/41 GEREĞİ AYRI BİR ALAN. Dayanak "işçi onayı"
            olmasa da (TİS hükmü gibi) onayın alınıp alınmadığı ayrıca
            kaydedilir; denetimde iki soru ayrı sorulur.
          */}
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={isciOnayi}
              onChange={(olay) => isciOnayiYaz(olay.target.checked)}
              className="rounded border-slate-300"
            />
            İşçilerin yazılı fazla çalışma onayı alındı
          </label>

          <CokSatir
            etiket="Gerekçe"
            deger={gerekce}
            degisti={gerekceYaz}
            ipucu="Zorunlu — hangi iş için, neden bu tarihte."
          />

          <CokSatir
            etiket="Açıklama (isteğe bağlı)"
            deger={aciklama}
            degisti={aciklamaYaz}
            satir={2}
          />

          <HataKutusu hata={izle.error} />
          <HataKutusu hata={kaydet.error} />

          {kontrolGecerli && <OnKontrolKutusu sonuc={kontrol.sonuc} />}

          {!kontrolGecerli && (
            <p className="text-xs text-slate-500">
              Kaydetmeden önce maliyet ve kota etkisini görmeniz gerekir. Tarih, saat veya kişi
              sayısını değiştirirseniz ön kontrol yenilenmelidir.
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Dugme tur="ikincil" tikla={kapat} pasif={kaydet.isPending} cocuk="Vazgeç" />
            <Dugme
              tur="ikincil"
              tikla={() => izle.mutate()}
              bekliyor={izle.isPending}
              pasif={!alanlarDolu}
              cocuk="Etkiyi göster"
            />
            <Dugme
              tikla={() => kaydet.mutate()}
              bekliyor={kaydet.isPending}
              pasif={!alanlarDolu || !kontrolGecerli}
              cocuk="Talep et"
            />
          </div>
        </div>
      }
    />
  );
}

function OnKontrolKutusu({ sonuc }: { sonuc: OnIzinOnKontrolSonucu }) {
  return (
    <section
      className={`space-y-2 rounded-md border p-3 text-sm ${
        // `kotaBlokluMu` SUNUCUDAN GELİR; eşiği panelde hesaplamıyoruz.
        // 270 saat aşımı yasal sınır (4857/41) ve kararı sunucu veriyor.
        sonuc.kotaBlokluMu ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-slate-50'
      }`}
    >
      <dl className="grid gap-3 sm:grid-cols-2">
        <div>
          <dt className="text-xs text-slate-500">Tahmini tutar</dt>
          {/* METİN olarak basılır — çevrilirse kuruş kaybı olur. */}
          <dd className="font-medium text-slate-900">{sonuc.tahminiTutar ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">En yüksek kota oranı</dt>
          <dd className={`font-medium ${sonuc.kotaBlokluMu ? 'text-red-700' : 'text-slate-900'}`}>
            {sonuc.enYuksekKotaOrani === null ? '—' : `%${sonuc.enYuksekKotaOrani}`}
          </dd>
        </div>
      </dl>

      {sonuc.kotaBlokluMu && (
        <p className="rounded border border-red-300 bg-white p-2 text-xs font-medium text-red-800">
          KOTA BLOKLU: 270 saat sınırı aşılacağı için bu ön izin onaylanamaz (4857/41).
        </p>
      )}

      {/*
        KOTASI DOLAN KİŞİLER AYRI LİSTE. Toplam içinde kaybolmamalı: bir
        kişi bile sınırı aşıyorsa o kişi mesaiye verilemez.
      */}
      {sonuc.kotasiDolanlar.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-red-800">
            Kotası dolmak üzere olanlar
          </h4>
          <ul className="mt-1 space-y-0.5 text-xs text-slate-800">
            {sonuc.kotasiDolanlar.map((k) => (
              <li key={k.adSoyad}>
                {k.adSoyad} — kullanım %{k.kullanimOrani}
              </li>
            ))}
          </ul>
        </div>
      )}

      {sonuc.uyarilar.length > 0 && (
        <ul className="list-disc space-y-0.5 pl-5 text-xs text-slate-700">
          {sonuc.uyarilar.map((u) => (
            <li key={u}>{u}</li>
          ))}
        </ul>
      )}
    </section>
  );
}
